import type { TipTapDoc } from "@compass/shared/schema/tiptapDoc";
import * as Dialog from "@radix-ui/react-dialog";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";
import CloseIcon from "assets/img/icons/header/close.svg?react";
import { UnifiedThreadComment } from "components/common/UnifiedThreadComment";
import { Button, DeleteCommentModal, ProseDocument } from "components/elements";
import { FavoriteButton } from "components/elements/FavoriteButton";
import { ThreadCommentForm } from "components/form/ThreadCommentForm";
import { useCurrentUser } from "hooks/useCurrentUser";
import type { ApiThreadComment } from "lib/threadCommentUtils";
import { trpc } from "lib/trpc";
import { VendorListing } from "@compass/shared/types";

// Vendor comment composer uses a minimal toolbar (bold/italic/underline/link)
// to match the redesign; the remaining editor features are turned off.
const VENDOR_EDITOR_FEATURES = {
    strike: false,
    heading: false,
    blockquote: false,
    bulletList: false,
    orderedList: false,
    indent: false,
    redoUndo: false,
    table: false,
} as const;

export const VendorDetailedEntry = ({
    vendor,
    isOpen,
    setIsOpen,
}: {
    vendor: VendorListing;
    isOpen: boolean;
    setIsOpen: (opened: boolean) => void;
}) => {
    const queryClient = trpc.useContext();

    // Get vendor thread + comments in one call
    const { data: vendorView } = trpc.vendors.getVendorView.useQuery(
        { vendorId: vendor.id },
        { enabled: isOpen },
    );
    const thread = vendorView?.thread;
    const threadComments = vendorView?.comments ?? [];

    const { isPending: isLoading, mutateAsync } = trpc.threads.createComment.useMutation();
    const { isPending: isUpdating, mutateAsync: updateCommentAsync } =
        trpc.threads.updateComment.useMutation();
    const deleteVendorCommentMutate = trpc.threads.deleteComment.useMutation();

    const [isCommentFormVisible, setIsCommentFormVisible] = useState<boolean>(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [commentToDelete, setCommentToDelete] = useState<number | null>(null);
    const [isCommentEdit, setIsCommentEdit] = useState<{
        show: boolean;
        item: ApiThreadComment | null;
    }>({
        show: false,
        item: null,
    });

    const currentUser = useCurrentUser();

    const handleDeleteComment = (commentId: number) => {
        try {
            deleteVendorCommentMutate.mutate(
                {
                    commentId: commentId,
                },
                {
                    onSuccess: () => {
                        enqueueSnackbar("Comment deleted successfully", { variant: "success" });
                        queryClient.vendors.getVendorView.invalidate({ vendorId: vendor.id });
                        queryClient.vendors.latest.invalidate();
                    },
                    onError: (e) => {
                        console.error(e);
                    },
                },
            );
        } catch (err) {
            console.error(err);
        }
    };

    const handleEditComment = (comment: any) => {
        setIsCommentEdit({ show: true, item: comment });
    };

    const handleCancelEdit = () => {
        setIsCommentEdit({ show: false, item: null });
    };

    const handleDeleteClick = (commentId: number) => {
        setCommentToDelete(commentId);
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = () => {
        if (commentToDelete) {
            handleDeleteComment(commentToDelete);
            setCommentToDelete(null);
        }
        setIsDeleteModalOpen(false);
    };

    const handleUpdateComment = (data: {
        contentJson: TipTapDoc;
        attachmentIds: string[];
        isAnonymous: boolean;
    }) => {
        if (!isCommentEdit.item) return;

        updateCommentAsync(
            {
                commentId: isCommentEdit.item.id,
                contentJson: data.contentJson,
                isPostedAnonymously: data.isAnonymous,
                attachmentIds: data.attachmentIds,
            },
            {
                onSuccess: () => {
                    enqueueSnackbar("Comment updated successfully.", { variant: "success" });
                    queryClient.vendors.getVendorView.invalidate({ vendorId: vendor.id });
                    queryClient.vendors.latest.invalidate();
                    setIsCommentEdit({ show: false, item: null });
                },
                onError: (e) => {
                    console.error(e);
                    enqueueSnackbar(`${e}`, { variant: "error" });
                },
            },
        );
    };

    const handleAddComment = (data: {
        contentJson: TipTapDoc;
        attachmentIds: string[];
        isAnonymous: boolean;
    }) => {
        if (!thread) return;

        mutateAsync(
            {
                threadId: thread.id,
                contentJson: data.contentJson,
                isPostedAnonymously: data.isAnonymous,
                attachmentIds: data.attachmentIds,
            },
            {
                onSuccess: () => {
                    enqueueSnackbar("Comment added successfully.", { variant: "success" });
                    queryClient.vendors.getVendorView.invalidate({ vendorId: vendor.id });
                    queryClient.vendors.latest.invalidate();
                    setIsCommentFormVisible(false);
                },
                onError: (e) => {
                    console.error(e);
                    enqueueSnackbar(`${e}`, { variant: "error" });
                },
            },
        );
    };

    return (
        <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
                <Dialog.Content className="___shadow-xl fixed top-10 left-1/2 z-50 flex max-h-[95vh] w-full max-w-2xl -translate-x-1/2 flex-col rounded-2xl bg-white p-5">
                    <div className="mx-[-20px] mb-5 flex items-center justify-between border-b border-[#C8C8C8] px-4 pb-4">
                        <div className="flex items-center gap-2">
                            <h2 className="text-left align-middle font-sans text-[24px] leading-[100%] font-bold tracking-normal text-[#424242]">
                                {vendor.name}
                            </h2>
                            <FavoriteButton target={{ type: "vendor", id: vendor.id }} size={18} />
                        </div>
                        <Dialog.Close asChild>
                            <button aria-label="Close">
                                <CloseIcon className="-mt-2 h-[30px] w-[30px]" />
                            </button>
                        </Dialog.Close>
                    </div>
                    <div className="space-y-4 flex-1 overflow-y-auto">
                        {/* Redesign: contact name as a heading, then vendor facts as
                            compact pills (category, website, email, address) instead of
                            a label/value table. Company Name is dropped — it's already the
                            modal title. Empty fields render no pill. */}
                        {vendor.contactName && (
                            <h3 className="font-sans text-[20px] leading-[100%] font-semibold tracking-normal text-[#424242]">
                                {vendor.contactName}
                            </h3>
                        )}

                        <div className="flex flex-wrap gap-2">
                            {vendor.category && (
                                <span className="rounded-lg bg-[#F2F2F2] px-4 py-2 font-sans text-[14px] text-[#424242]">
                                    {vendor.category}
                                </span>
                            )}
                            {vendor.website && (
                                <a
                                    href={vendor.website}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="rounded-lg bg-[#F2F2F2] px-4 py-2 font-sans text-[14px] text-[#424242] hover:bg-[#E8E8E8]"
                                >
                                    {vendor.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                                </a>
                            )}
                            {vendor.contactEmail && (
                                <a
                                    href={`mailto:${vendor.contactEmail}`}
                                    className="rounded-lg bg-[#F2F2F2] px-4 py-2 font-sans text-[14px] text-[#424242] hover:bg-[#E8E8E8]"
                                >
                                    {vendor.contactEmail}
                                </a>
                            )}
                            {vendor.contactAddress && (
                                <span className="rounded-lg bg-[#F2F2F2] px-4 py-2 font-sans text-[14px] text-[#424242]">
                                    {vendor.contactAddress}
                                </span>
                            )}
                        </div>

                        {vendor.notesContentJson && (
                            <div>
                                <p className="mb-3 text-left align-middle font-sans text-[20px] leading-[100%] font-semibold text-[#424242]">
                                    About the vendor
                                </p>
                                <div className="no-scrollbar font-roboto ___shadow-inner max-h-40 overflow-y-auto rounded-xl border border-[#92CEE4] bg-[#DFF1FA] p-5 text-left align-middle text-[16px] leading-[127%] font-normal text-[#424242]/90">
                                    <ProseDocument doc={vendor.notesContentJson} />
                                </div>
                            </div>
                        )}
                        {threadComments.length > 0 && (
                            <div className="mx-[-20px] border-t border-[#EAEAEA]" />
                        )}
                        <div className="space-y-3">
                            {[...threadComments]
                                .sort(
                                    (a, b) =>
                                        new Date(b.createdAt).getTime() -
                                        new Date(a.createdAt).getTime(),
                                )
                                .map((comment) => (
                                    // Redesign: each vendor comment sits in a light-gray
                                    // rounded card. Scoped here (not in the shared
                                    // UnifiedThreadComment) so discussion/social/blog are
                                    // unaffected.
                                    <div key={comment.id} className="rounded-xl bg-[#F7F7F7] p-4">
                                        <UnifiedThreadComment
                                            comment={comment}
                                            context="vendor"
                                            currentUser={currentUser}
                                            onEdit={() => handleEditComment(comment)}
                                            onDelete={() => handleDeleteClick(comment.id)}
                                        />
                                    </div>
                                ))}
                        </div>
                    </div>

                    {/* Composer stays hidden behind the "Add comment" button; clicking it
                        reveals the redesigned composer (identity row + toolbar + Share). */}
                    {!isCommentFormVisible && !isCommentEdit.show && (
                        <div className="flex justify-end">
                            <Button variant="primary" onClick={() => setIsCommentFormVisible(true)}>
                                Add comment
                            </Button>
                        </div>
                    )}

                    {isCommentEdit.show && isCommentEdit.item && (
                        <div className="-mx-5 mt-4 px-5 pt-4 shadow-[0_-8px_16px_-10px_rgba(0,0,0,0.18)]">
                            <ThreadCommentForm
                                context="vendor"
                                mode="edit"
                                initialContentJson={isCommentEdit.item.contentJson}
                                initialAttachments={isCommentEdit.item.attachments}
                                initialIsAnonymous={isCommentEdit.item.isPostedAnonymously}
                                onSubmit={handleUpdateComment}
                                onCancel={handleCancelEdit}
                                showCancel
                                isLoading={isUpdating}
                                submitLabel="Update Comment"
                                placeholder="Edit your comment..."
                                showUserIdentity
                                componentType="vendorComment"
                                editorFeatures={VENDOR_EDITOR_FEATURES}
                                compactAttachments
                                toolbarBgColor="bg-[#eeeeee]"
                            />
                        </div>
                    )}

                    {isCommentFormVisible && !isCommentEdit.show && (
                        <div className="-mx-5 mt-4 px-5 pt-4 shadow-[0_-8px_16px_-10px_rgba(0,0,0,0.18)]">
                            <ThreadCommentForm
                                context="vendor"
                                onSubmit={handleAddComment}
                                onCancel={() => setIsCommentFormVisible(false)}
                                showCancel
                                isLoading={isLoading}
                                submitLabel="Share"
                                placeholder="Share something about the vendor..."
                                showUserIdentity
                                componentType="vendorComment"
                                editorFeatures={VENDOR_EDITOR_FEATURES}
                                compactAttachments
                                toolbarBgColor="bg-[#eeeeee]"
                            />
                        </div>
                    )}
                    <DeleteCommentModal
                        isOpen={isDeleteModalOpen}
                        setIsOpen={setIsDeleteModalOpen}
                        onDelete={confirmDelete}
                    />
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};
